import ApiService from '@/services/ApiService';
import BranchesAndTeamsService from '@/services/BranchesAndTeamsService';

class DashboardService {
  static async getPendingEvents(query) {
    let composedURL = `/api3/reports/events_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getUnassignedInquiries(query) {
    let composedURL = `/api3/reports/unassigned_inquiries_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getVisitsToConfirm(query) {
    let composedURL = `/api3/reports/visits_to_confirm_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPropertiesPendingReview({ query } = {}) {
    let composedURL = `/api3/current_status/pending_review_card/?entity=property`;
    if (query) {
      composedURL += `&${query.replace(/^\?/, '')}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getDevelopmentsPendingReview({ query } = {}) {
    let composedURL = `/api3/current_status/pending_review_card/?entity=development`;
    if (query) {
      composedURL += `&${query.replace(/^\?/, '')}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getQprecoMarketplace({ query } = {}) {
    let composedURL = `/api3/reports/marketplace_price_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getMarketplaceRegistrationQuality({ query } = {}) {
    let composedURL = `/api3/reports/marketplace_completeness_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPendingContacts(query) {
    let composedURL = `/api3/reports/pending_contacts_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPendingLeads(query) {
    let composedURL = `/api3/reports/expiring_opportunities_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPendingReservations(query) {
    let composedURL = `/api3/reports/expiring_reservations_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPortalsNotifications(query) {
    let composedURL = `/api3/reports/portal_alerts_errors_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getExclusivityProperties(query) {
    let composedURL = `/api3/reports/exclusive_properties_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getExpiringRentProperties(query) {
    let composedURL = `/api3/reports/expiring_rent_properties/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getContactChatChannel(contactId) {
    const response = await ApiService.post(
      `/api3/tokko_chat/${contactId}/chat_or_sender`
    );
    return response;
  }

  static async getCompanyAgents() {
    const response = await ApiService.get('/api3/agent/?only_actives=true');
    return response;
  }

  static async getCompanyBranches() {
    const response = await ApiService.get('/api3/company/branch');
    return response;
  }

  // `GET /api3/company/teams/` is open to any authenticated company user
  // (same as agents/branches catalogs); create/update/delete stay admin-only.
  // The dashboard still only mounts `TeamsDropdown` for admins, but a non-admin
  // caller would no longer 403 on a plain list.
  static async getCompanyTeams() {
    const response = await BranchesAndTeamsService.listTeams({});
    return { teams: response.results || [] };
  }

  static async getOverviewSummary(query) {
    let composedURL = `/api3/reports/current_status/summary/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewContactsSource(query) {
    let composedURL = `/api3/reports/current_status/contact/tag_groups/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewLeads(query) {
    let composedURL = `/api3/reports/current_status/contact/opportunities/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewPendingEvents(query) {
    let composedURL = `/api3/reports/current_status/contact/pending_events/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewOperationStatistics({ statusId, query }) {
    let composedURL = `/api3/reports/current_status/property/operations_statistics/`;
    if (query) {
      composedURL += `${query}&status=${statusId}`;
    } else {
      composedURL += `?status=${statusId}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewVisitEvents(query) {
    let composedURL = `/api3/reports/current_status/contact/visit_events/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewPropertiesOperations({ statusId, query }) {
    let composedURL = `/api3/reports/current_status/property/operations_totals/`;
    if (query) {
      composedURL += `${query}&status=${statusId}`;
    } else {
      composedURL += `?status=${statusId}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewPropertiesTypes({ statusId, operationId, query }) {
    let composedURL = `/api3/reports/current_status/property/types_totals/`;
    if (query) {
      composedURL += `${query}&status=${statusId}&operation_types=${operationId}`;
    } else {
      composedURL += `?status=${statusId}&operation_types=${operationId}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewPropertiesLocations({
    statusId,
    operationId,
    query,
  }) {
    let composedURL = `/api3/reports/current_status/property/locations_totals/`;
    if (query) {
      composedURL += `${query}&status=${statusId}&operation_types=${operationId}`;
    } else {
      composedURL += `?status=${statusId}&operation_types=${operationId}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewConstructionAge({ statusId, operationId, query }) {
    let composedURL = `/api3/reports/current_status/property/age_buckets_totals/`;
    if (query) {
      composedURL += `${query}&status=${statusId}&operation_types=${operationId}`;
    } else {
      composedURL += `?status=${statusId}&operation_types=${operationId}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewEntryDate({ statusId, operationId, query }) {
    let composedURL = `/api3/reports/current_status/property/availability_date_buckets_totals/`;
    if (query) {
      composedURL += `${query}&status=${statusId}&operation_types=${operationId}`;
    } else {
      composedURL += `?status=${statusId}&operation_types=${operationId}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewStaleProperties(query) {
    let composedURL = `/api3/reports/stale_properties_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewStaleContacts(query) {
    let composedURL = `/api3/reports/stale_contacts_card/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewDevelopmentsTypes(query) {
    let composedURL = `/api3/reports/current_status/development/types_totals/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewDevelopmentsLocations(query) {
    let composedURL = `/api3/reports/current_status/development/locations_totals/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getOverviewTasks(query) {
    let composedURL = `/api3/schedule/calendar_task/dashboard/`;
    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceReceivedLeadsCard({ query }) {
    let composedURL = `/api3/reports/received_leads_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceAddedPropertiesCard({ query }) {
    let composedURL = `/api3/reports/new_property_availability_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceDoneVisitsCard({ query }) {
    let composedURL = `/api3/reports/done_events_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceDoneEventsCard({ query }) {
    let composedURL = `/api3/reports/events_made_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceNewContactsCard({ query }) {
    let composedURL = `/api3/reports/new_contacts_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceConcretedOperationsCard({ query }) {
    let composedURL = `/api3/reports/concreted_operations_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceResponseTimeCard({ query }) {
    let composedURL = `/api3/reports/response_time_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceConversionRateCard({ query }) {
    let composedURL = `/api3/reports/conversion_rate_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceClosedLeadsCard({ query }) {
    let composedURL = `/api3/reports/closed_leads_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceReservationCard({ query }) {
    let composedURL = `/api3/reports/reservation_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceProductionRateCard({ query }) {
    let composedURL = `/api3/reports/production_rate_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceVisitEventsCard({ query }) {
    let composedURL = `/api3/reports/visit_events_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceConversionTimeCard({ query }) {
    let composedURL = `/api3/reports/conversion_time_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceMedianResponseTimeCard({ query }) {
    let composedURL = `/api3/reports/median_response_time_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformancePropertyConsultationsCard({ query }) {
    let composedURL = `/api3/reports/property_consultations_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceDevelopmentConsultationsCard({ query }) {
    let composedURL = `/api3/reports/development_consultations_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceReceivedLeadsMiniCard({ query }) {
    let composedURL = `/api3/reports/received_leads_mini_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceConversionRateMiniCard({ query }) {
    let composedURL = `/api3/reports/conversion_rate_mini_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceDoneEventsMiniCard({ query }) {
    let composedURL = `/api3/reports/done_events_mini_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceNewContactsMiniCard({ query }) {
    let composedURL = `/api3/reports/new_contacts_mini_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceAddedPropertiesMiniCard({ query }) {
    let composedURL = `/api3/reports/new_property_availability_mini_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceConcretedOperationsMiniCard({ query }) {
    let composedURL = `/api3/reports/concreted_operations_mini_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceConversionTimeMiniCard({ query }) {
    let composedURL = `/api3/reports/conversion_time_mini_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceResponseTimeMiniCard({ query }) {
    let composedURL = `/api3/reports/response_time_mini_card`;

    if (query) {
      composedURL += `${query}`;
    }
    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceNewContactsTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/new_contacts_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceInquiriesReceivedTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/received_inquiries_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceDoneVisitsTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/visit_events_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceDoneEventsTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/done_events_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceConversionTimeTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/conversion_time_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceResponseTimeTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/response_time_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceAddedPropertiesTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/new_property_availability_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceConversionRateTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/conversion_rate_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceConcretedOperationsTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/concreted_operations_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformancePropertyConsultationsTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/property_consultations_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceDevelopmentConsultationsTable({
    query,
    tableQuery,
  }) {
    let composedURL = `/api3/reports/development_consultations_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceClosedLeadsTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/closed_leads_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceReservationsCreatedTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/reservation_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceProductionRateTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/production_rate_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceAgentsPerformanceTable({ query, tableQuery }) {
    let composedURL = `/api3/reports/agent_performance_table`;

    if (query) {
      composedURL += `${query}`;
    }

    if (tableQuery) {
      composedURL += `${tableQuery}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceConcretedOperationsEvolutionCard({ query }) {
    let composedURL = `/api3/reports/concreted_operations_evolution_card`;

    if (query) {
      composedURL += `${query}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async getPerformanceConcretedOperationsSourceCard({ query }) {
    let composedURL = `/api3/reports/concreted_operations_source_card`;

    if (query) {
      composedURL += `${query}`;
    }

    const response = await ApiService.get(composedURL);
    return response;
  }

  static async putDashboardConfig({ payload }) {
    const composedURL = `/api3/dashboard/config`;

    const response = await ApiService.put(composedURL, payload);
    return response;
  }

  static async startReportsTableExcelDownload(downloadPath) {
    const response = await ApiService.get(downloadPath);
    return response;
  }

  static async trackReportsTableExcelDownload(tracking) {
    const response = await ApiService.get(
      `/api3/reports/track_xls_download?tracking=${encodeURIComponent(
        tracking
      )}`
    );
    return response;
  }

  static async deleteReportsTableExcelDownload(tracking) {
    const response = await ApiService.get(
      `/api3/reports/delete_track_xls_download?tracking=${encodeURIComponent(
        tracking
      )}`
    );
    return response;
  }
}

export default DashboardService;
